import { isTRPCClientError } from '@trpc/client';

export interface EntityDeleteBlocked {
  linkedRiskCount: number;
  childEntityCount: number;
}

/**
 * The data-layer rejects a blocked entity delete with a 409 whose error message
 * is a JSON envelope string:
 *
 *   {"code":"ENTITY_DELETE_BLOCKED","message":"...","linkedRiskCount":N,"childEntityCount":M}
 *
 * The tRPC layer maps that 409 to a TRPCClientError with `data.code === 'CONFLICT'`
 * and `error.message` set to the JSON envelope string. This helper recovers the
 * blocked-deletion counts from the tRPC path so the consumer can render the
 * blocked-deletion alert (mirrors the Apollo path's `graphQLErrors[0].extensions`).
 *
 * Returns `null` for any error that is NOT a well-shaped ENTITY_DELETE_BLOCKED
 * conflict — non-blocked errors are never swallowed.
 */
export const tryExtractEntityDeleteBlocked = (
  error: unknown
): EntityDeleteBlocked | null => {
  if (!isTRPCClientError(error)) {
    return null;
  }
  if (error.data?.code !== 'CONFLICT') {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(error.message);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'code' in parsed &&
      parsed.code === 'ENTITY_DELETE_BLOCKED'
    ) {
      const linkedRiskCount =
        'linkedRiskCount' in parsed &&
        typeof parsed.linkedRiskCount === 'number'
          ? parsed.linkedRiskCount
          : 0;
      const childEntityCount =
        'childEntityCount' in parsed &&
        typeof parsed.childEntityCount === 'number'
          ? parsed.childEntityCount
          : 0;

      return { linkedRiskCount, childEntityCount };
    }
  } catch {
    return null;
  }

  return null;
};
